/**
 * The codemirror text editor.
 * Use the code state to inject code into codemirror using an event.
 * all state is then managed internally by Codemirror.
 * Codemirror should be the source of truth for the what's being previewed.
 * @module CodeMirror Editor
 * @category CodeMirror
 */

import {
	type Completion,
	acceptCompletion,
	autocompletion,
	closeCompletion,
	moveCompletionSelection,
	startCompletion,
} from "@codemirror/autocomplete";
import {
	history,
	historyKeymap,
	indentWithTab,
	standardKeymap,
	undo,
} from "@codemirror/commands";
import {
	foldAll,
	foldState,
	syntaxHighlighting,
	syntaxTree,
	unfoldAll,
} from "@codemirror/language";
import { search, searchKeymap } from "@codemirror/search";
import { Compartment, EditorState, Prec, Transaction } from "@codemirror/state";
import {
	EditorView,
	type ViewUpdate,
	highlightActiveLineGutter,
	keymap,
} from "@codemirror/view";
import {
	type SDK_Filter,
	type SDK_Fonts,
	type SDK_Stack,
	TextDecorations,
	colors,
	text,
	getEnglishToLocaleKeywordMap,
	getEnglishToLocaleModifierMap,
	getLocale,
	modifierObject,
	strings,
	translateJadleToEnglish,
	translateJadleToLocale,
} from "@quickpoint.me/shared";
import {
	QPTheme,
	QpHighlightStyle,
	rYamlParser,
	setLocaleTokenMap,
} from "@quickpoint.me/shared/editor";
import { useEffect, useRef } from "react";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import {
	addPreviewToOptions,
	onAutoCompleteTransaction,
	rYamlCompletions,
} from "../../lib/editor/autoComplete";
import {
	canApplyTextFormatting,
	insertCharacterAtCursor,
	insertWordAt,
	wrapSelection,
} from "../../lib/editor/helperPanel";
import {
	type AppVars,
	appVarsEffect,
	appVarsStateField,
	audioFilesEffect,
	audioFilesStateField,
	backLinksEffect,
	backLinksStateField,
	dataFilesEffect,
	dataFilesStateField,
	defaultFilterColors,
	editorIsNewStateField,
	filterColorsEffect,
	filterColorsStateField,
	filterNamesEffect,
	filterNamesStateField,
	imageFilesEffect,
	imageFilesStateField,
	loadedFilterFontsEffect,
	loadedFilterFontsStateField,
	mousedOverHelperField,
} from "../../lib/editor/recoilStatesForCodeMirror";
import { clearCode, updateCode } from "../../lib/editor/textHelpers";
import {
	CurrentKeyword,
	EditorIsClean,
	HostId,
	PreviewMode,
	ViewerPageIndex,
} from "../../state/atoms";
import { LocaleAtom } from "../../state/localeAtom";
import {
	CanApplyTextFormatting,
	CodeIsFolded,
	CodeMirrorIsReady,
	SideBarTabAtom,
} from "../../state/uiAtoms";

import type { QpFile } from "@quickpoint.me/shared/src/gql/generated/schema";
import styled from "styled-components";

import {
	currentLineGutter,
	markModifierLines,
	markReadLines,
	pageLines,
	pageNumbersGutter,
	textEffectMarkers,
} from "@quickpoint.me/shared/editor";
import { useSaveStack } from "../../hooks/useSaveStack";
import { contextualKeywordsState } from "../../lib/editor/contextualKeywordsState";
import { goToPage } from "../../lib/editor/events";
import foldOnIndent from "../../lib/editor/fold";
import pageCounter from "../../lib/editor/pageCounter";
import { simpleLinter } from "../../lib/editor/simpleLinter";
import { rangeUnderlining } from "../../lib/editor/underline";
import { CodeMirrorCodeState } from "../../state/codeAtom";
import { StackAtomFamily } from "../../state/stackAtoms";
import { StackSelectorFamily } from "../../state/stackSelectors";
import { moveCursorToPageIndex } from "../controls/codeMirrorNavigationPanel";

const StyledCodeMirror = styled.div<{ $isInPortrait: boolean }>`
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;

    #codemirror-editor-wrapper {
        flex: 1;
        min-height: 0;
        background-color: ${colors.white}
    }

    .cm-editor {
        height: 100%;
    }
`;

function isViewUpdateFromUserInput(viewUpdate: ViewUpdate) {
	// Make sure document has changed, ensuring user events like selections don't count.

	// Check transactions for any that are direct user input, not changes from Y.js or another extension.
	for (const transaction of viewUpdate.transactions) {
		// Not using Transaction.isUserEvent because that only checks for a specific User event type ( "input", "delete", etc.). Checking the annotation directly allows for any type of user event.
		const userEventType = transaction.annotation(Transaction.userEvent);
		if (userEventType) return userEventType;
	}

	return false;
}

export function createFontCss(font: string) {
	const [styleSheet] = window.document.styleSheets;

	const className = `options-font-${font.replaceAll(" ", "-")}`;
	styleSheet.insertRule(
		`.${className} { font-size: .9rem; font-family: "${font}";  }`,
		styleSheet.cssRules.length,
	);
	return className;
}

// Create a compartment for history
const historyCompartment = new Compartment();

function CodeMirror({
	stackCfKey,
	isInPortrait,
}: { stackCfKey: string; isInPortrait: boolean }) {
	const hostId = useRecoilValue(HostId);
	const debounceAutoCompleteTime = 250;
	const debounceTime = 15;
	const setCanApplyTextFormatting = useSetRecoilState(CanApplyTextFormatting);
	const setCodeIsFolded = useSetRecoilState(CodeIsFolded);
	const setCodeMirrorCodeState = useSetRecoilState(
		CodeMirrorCodeState(stackCfKey),
	);
	const setCodeMirrorIsReady = useSetRecoilState(CodeMirrorIsReady);
	const setCurrentKeyword = useSetRecoilState(CurrentKeyword);
	const setEditorIsClean = useSetRecoilState(EditorIsClean);
	const setPreviewMode = useSetRecoilState(PreviewMode);
	const setViewerPageIndex = useSetRecoilState(ViewerPageIndex);
	const setSideBarTab = useSetRecoilState(SideBarTabAtom);
	const save = useSaveStack();
	const debouncedAutoComplete = useRef<NodeJS.Timeout>();
	const debouncedFn = useRef<NodeJS.Timeout>();

	const editorViewRef = useRef<EditorView | null>(null);
	const editorDomRef = useRef<HTMLDivElement | null>(null);
	const historyReset = useRef(false);

	const locale = useRecoilValue(LocaleAtom);
	const prevLocaleRef = useRef(locale);

	const codeMirrorReady = useRef(false); // This flag is captured by the listener from the first render.

	const codeMirrorIsReady = EditorView.updateListener.of((view: ViewUpdate) => {
		// is Code Mirror ready?
		if (codeMirrorReady.current) return;
		if (syntaxTree(view.state).length === view.state.doc.length) {
			setCodeMirrorIsReady(Math.random());
			codeMirrorReady.current = true;
		}
	});

	const setNewCode = useRecoilCallback(
		({ set, snapshot }) =>
			(newCode: string) => {
				if (!stackCfKey) return;
				const loadable = snapshot.getLoadable(StackAtomFamily(stackCfKey));
				if (loadable.state !== "hasValue") return;
				set(StackSelectorFamily(stackCfKey), (oldStack: SDK_Stack) => {
					const newStack = { ...oldStack };
					newStack.code = newCode;
					return newStack;
				});
			},
		[stackCfKey],
	);

	const onTransaction = () => {
		return EditorView.updateListener.of((update) => {
			if (!codeMirrorReady.current) return;
			if (!update.docChanged) return;
			const transaction = update.transactions[0];
			if (transaction) {
				const doc: string = update.state.toJSON().doc;
				// Translate locale keywords back to English for storage/API
				const englishDoc = translateJadleToEnglish(doc);
				clearTimeout(debouncedFn.current);
				debouncedFn.current = setTimeout(() => {
					setPreviewMode(false);
					setCodeMirrorCodeState(englishDoc);

					if (transaction.isUserEvent("delete")) {
						setEditorIsClean(false);
						setNewCode(englishDoc);
						setSideBarTab("help");
					}
					if (transaction.isUserEvent("input")) {
						setEditorIsClean(false);
						setNewCode(englishDoc);
						setSideBarTab("help");
					}
				}, debounceTime);
			}
		});
	};

	const openCompletionOnTab = () => {
		return EditorView.updateListener.of((update) => {
			const selection = update.state.selection.ranges[0];
			const pos = selection ? selection.from : 0;
			const currentLine = update.state.doc.lineAt(pos);
			const transaction = update.transactions[0];
			clearTimeout(debouncedAutoComplete.current);
			if (transaction?.isUserEvent("input") && currentLine.text === "  ") {
				debouncedAutoComplete.current = setTimeout(() => {
					startCompletion(update.view);
				}, debounceAutoCompleteTime);
			}
		});
	};

	const userEvents = () => {
		return EditorView.domEventHandlers({
			[strings.events.clearCode]: (_e, view) => {
				//console.log("Event: clearCode called");
				clearCode(view);
			},
			[strings.events.insertCharacter]: (
				e: { detail: { character: string; openAutoComplete: boolean } },
				view,
			) => {
				//console.log("Event: insertCharacter called");
				if (e.detail.character) {
					insertCharacterAtCursor(
						e.detail.character,
						view.state,
						view,
						e.detail.openAutoComplete,
					);
				}
			},

			[strings.events.insertWord]: (
				e: {
					detail: {
						word: string;
						newLine: boolean | undefined;
						insertAtLineNumber: number | false;
						isModifier: boolean | undefined;
					};
				},
				view,
			) => {
				//console.log("Event: insertWord called");
				const transaction = {
					word: e.detail.word,
					state: view.state,
					view: view,
					newLine: e.detail.newLine,
					insertAtLineNumber: e.detail.insertAtLineNumber,
					isModifier: e.detail.isModifier,
				};
				insertWordAt(transaction);
			},

			[strings.events.wrapSelection]: (
				e: { detail: { textModifier: string } },
				view,
			) => {
				//console.log("Event: wrapSelection called");
				wrapSelection(e.detail.textModifier, view);
			},
			[strings.events.fold]: (
				e: { detail: { codeIsFolded: boolean } },
				view,
			) => {
				//console.log("Event: fold called");
				e.detail.codeIsFolded ? unfoldAll(view) : foldAll(view);
			},

			[strings.events.updateBacklinks]: (
				e: { detail: { backLinks: string[] } },
				view,
			) => {
				//console.log("Event: updateBacklinks called");
				view.dispatch({
					effects: backLinksEffect.of(e.detail.backLinks),
				});
			},

			[strings.events.undo]: (_e, view) => {
				//console.log("Event: undo called");
				undo(view);
			},

			[strings.events.updateCode]: (e: {
				detail: { code: SDK_Stack["code"]; isLoad?: boolean };
			}) => {
				//console.log("Event: updateCode called");
				if (editorViewRef.current) {
					// This check prevents a loop where the editor's own updates are sent back to it,
					// which can cause the cursor to jump to the beginning.
					// Compare incoming English code against the English-translated CM doc
					// (since the CM doc is in the user's locale).
					if (
						e.detail.code &&
						e.detail.code ===
							translateJadleToEnglish(
								editorViewRef.current.state.doc.toString(),
							)
					) {
						return;
					}

					if (e.detail.code) {
						// Translate English keywords to the user's locale for display.
						// Loads (stack switches) must not register as user input or the
						// loaded code is written back into the currently keyed stack atom.
						updateCode(
							editorViewRef.current,
							translateJadleToLocale(e.detail.code),
							{
								asUserInput: !e.detail.isLoad,
							},
						);
					} else {
						clearCode(editorViewRef.current);
					}

					if (!historyReset.current) {
						editorViewRef.current.dispatch({
							effects: historyCompartment.reconfigure([]),
						});
						editorViewRef.current.dispatch({
							effects: historyCompartment.reconfigure([
								history({ newGroupDelay: 0 }),
							]),
						});
						historyReset.current = true;
					}
				}
			},

			[strings.events.updateAppVars]: (
				e: { detail: { appVars: AppVars } },
				view,
			) => {
				//console.log("Event: updateAppVars called");
				view.dispatch({
					effects: appVarsEffect.of(e.detail.appVars),
				});
			},

			[strings.events.updateAudioFiles]: (
				e: { detail: { allAudioFiles: QpFile[] } },
				view,
			) => {
				//console.log("Event: updateAudioFiles called");
				view.dispatch({
					effects: audioFilesEffect.of(e.detail.allAudioFiles),
				});
			},

			[strings.events.updateImageFiles]: (
				e: { detail: { allImageFiles: QpFile[] } },
				view,
			) => {
				//console.log("Event: updateImageFiles called");
				view.dispatch({
					effects: imageFilesEffect.of(e.detail.allImageFiles),
				});
			},

			[strings.events.updateDataFiles]: (
				e: { detail: { allDataFiles: QpFile[] } },
				view,
			) => {
				//console.log("Event: updateDataFiles called");
				view.dispatch({
					effects: dataFilesEffect.of(e.detail.allDataFiles),
				});
			},

			[strings.events.updateFilterColors]: (
				e: { detail: { filterColors: SDK_Filter["colors"] } },
				view,
			) => {
				//console.log("Event: updateFilterColors called");
				view.dispatch({
					effects: filterColorsEffect.of(e.detail.filterColors),
				});
			},
			[strings.events.updateFilterNames]: (
				e: { detail: { filterNames: string[] } },
				view,
			) => {
				//console.log("Event: updateFilterNames called");
				view.dispatch({
					effects: filterNamesEffect.of(e.detail.filterNames),
				});
			},
			[strings.events.updateLoadedFilterFonts]: (
				e: { detail: { loadedFonts: SDK_Fonts } },
				view,
			) => {
				//console.log("Event: updateLoadedFilterFonts called");
				view.dispatch({
					effects: loadedFilterFontsEffect.of(e.detail.loadedFonts),
				});
			},

			[strings.events.updateStackCfKeys]: (e: { detail: string[] }, view) => {
				//console.log("Event: updateStackCfKeys called");
				view.dispatch({
					effects: backLinksEffect.of(e.detail),
				});
			},

			[strings.events.moveCursorToPageIndex]: (
				e: { detail: { pageIdx: number } },
				view,
			) => {
				//console.log("Event: moveCursorToPageIndex called");
				if (e.detail.pageIdx) moveCursorToPageIndex(view, e.detail.pageIdx);
			},

			[strings.events.aiStreamStart]: (_e, view) => {
				// Clear editor and set aria-busy for screen readers
				clearCode(view);
				view.contentDOM.setAttribute("aria-busy", "true");
				view.contentDOM.focus();
			},

			[strings.events.aiStreamChunk]: (
				e: { detail: { chunk: string } },
				view,
			) => {
				// Insert chunk character by character with user event annotation
				const chunk = e.detail.chunk;
				if (!chunk) return;

				const pos = view.state.doc.length;
				view.dispatch({
					changes: { from: pos, insert: chunk },
					selection: { anchor: pos + chunk.length },
					annotations: Transaction.userEvent.of("input.type"),
				});
			},

			[strings.events.aiStreamEnd]: (_e, view) => {
				// Clear aria-busy when streaming ends
				view.contentDOM.removeAttribute("aria-busy");
			},
		});
	};

	const setRecoilStateFromCodeMirror = () => {
		return EditorView.updateListener.of((view: ViewUpdate) => {
			const field = view.state.field(foldState, false);
			if (field) {
				setCodeIsFolded(!(field.size === 0));
			}

			// Track whether text formatting can be applied (for B/I/U buttons)
			setCanApplyTextFormatting(canApplyTextFormatting(view.state));

			for (const tr of view.transactions) {
				if (tr.isUserEvent("delete")) {
					setCurrentKeyword("topLevel");
				} else {
					const keyword = view.state.field(contextualKeywordsState);
					setCurrentKeyword(keyword);
				}
			}
		});
	};

	const goToSelectedPageNumber = EditorView.updateListener.of((viewUpdate) => {
		const userEvent = isViewUpdateFromUserInput(viewUpdate);
		if (userEvent) {
			const page = viewUpdate.state.field(pageCounter);
			goToPage(hostId, "QP", page);
			goToPage(hostId, "preview", page);
			setViewerPageIndex(page);
		}
	});

	const saveAndReturnTrue = () => {
		save();
		return true;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Empty dependency array ensures this runs only on mount and unmount
	useEffect(() => {
		if (editorDomRef.current && !editorViewRef.current) {
			// Configure the Lezer tokenizer for the current locale
			const locale = getLocale();
			if (locale && locale !== "en") {
				setLocaleTokenMap(
					getEnglishToLocaleKeywordMap(locale),
					getEnglishToLocaleModifierMap(locale),
				);
			}

			const initialState = EditorState.create({
				doc: "",
				extensions: [
					keymap.of([
						...standardKeymap,
						...historyKeymap,
						...searchKeymap,
						indentWithTab,
					]),
					Prec.highest(
						keymap.of([
							// { key: "Tab", run: acceptCompletion },
							{ key: "Enter", run: acceptCompletion },
							{ key: "Escape", run: closeCompletion },
							{ key: "ArrowDown", run: moveCompletionSelection(true) },
							{ key: "ArrowUp", run: moveCompletionSelection(false) },
							{ key: "PageDown", run: moveCompletionSelection(true, "page") },
							{ key: "PageUp", run: moveCompletionSelection(false, "page") },
							{ key: "Mod-s", run: saveAndReturnTrue, preventDefault: true },
							{ key: "Mod-m", run: startCompletion },
							{
								key: "Mod-b",
								run: (view) => {
									wrapSelection(TextDecorations.bold, view);
									return true;
								},
								preventDefault: true,
							},
							{
								key: "Mod-i",
								run: (view) => {
									wrapSelection(TextDecorations.italic, view);
									return true;
								},
								preventDefault: true,
							},
							{
								key: "Mod-u",
								run: (view) => {
									wrapSelection(TextDecorations.underline, view);
									return true;
								},
								preventDefault: true,
							},
						]),
					),
					rYamlParser(rYamlCompletions),
					audioFilesStateField,
					appVarsStateField,
					autocompletion({
						closeOnBlur: false,
						aboveCursor: isInPortrait, // Note: uses isInPortrait from the initial render
						optionClass: (completion: Completion) => {
							if (completion.type === modifierObject["font:"].keyword) {
								return createFontCss(completion.label);
							}
							return "none";
						},
						addToOptions: [{ render: addPreviewToOptions, position: 20 }],
					}),
					backLinksStateField,
					codeMirrorIsReady, // This listener is created once and captures `codeMirrorReady` from the initial render
					contextualKeywordsState,
					dataFilesStateField,
					editorIsNewStateField,
					EditorView.contentAttributes.of({
						spellcheck: "true",
						autocapitalize: "on",
						// accessible name for the editor textbox (4.1.2)
						"aria-label": text.ui.editorLabel,
					}),
					EditorView.lineWrapping,
					filterColorsStateField.init(() => defaultFilterColors),
					filterNamesStateField,
					foldOnIndent(),
					loadedFilterFontsStateField,
					onAutoCompleteTransaction(),
					pageCounter,
					pageLines(),
					pageNumbersGutter,
					currentLineGutter,
					goToSelectedPageNumber, // This listener is created once
					highlightActiveLineGutter(),
					userEvents(), // `userEvents()` is called once, its handlers will use `editorViewRef.current`
					historyCompartment.of(history({ newGroupDelay: 0 })),
					imageFilesStateField,
					markModifierLines(),
					markReadLines(),
					mousedOverHelperField,
					onTransaction(), // This listener is created once
					openCompletionOnTab(), // This listener is created once
					QPTheme,
					rangeUnderlining(),
					search({ top: true }),
					setRecoilStateFromCodeMirror(), // This listener is created once
					simpleLinter,
					syntaxHighlighting(QpHighlightStyle, { fallback: true }),
					textEffectMarkers,
				],
			});

			const view = new EditorView({
				state: initialState,
				parent: editorDomRef.current,
			});
			editorViewRef.current = view;
		}

		return () => {
			if (editorViewRef.current) {
				if (process.env.NODE_ENV !== "test") {
					// vitest breaks if destroy is called
					editorViewRef.current.destroy();
				}
				editorViewRef.current = null;
			}
		};
	}, []); // Empty dependency array ensures this runs only on mount and unmount

	// When locale changes, update the tokenizer and re-translate editor content
	useEffect(() => {
		const prev = prevLocaleRef.current;
		if (prev === locale) return;
		prevLocaleRef.current = locale;

		// Update the Lezer tokenizer for the new locale
		if (locale === "en") {
			setLocaleTokenMap();
		} else {
			setLocaleTokenMap(
				getEnglishToLocaleKeywordMap(locale),
				getEnglishToLocaleModifierMap(locale),
			);
		}

		// Re-translate the editor content
		if (editorViewRef.current) {
			const doc = editorViewRef.current.state.doc.toString();
			const english = translateJadleToEnglish(doc, prev);
			const translated = translateJadleToLocale(english, locale);
			if (translated !== doc) {
				// display-only retranslation, not user input
				updateCode(editorViewRef.current, translated, { asUserInput: false });
			}
		}
	}, [locale]);

	return (
		<StyledCodeMirror $isInPortrait={isInPortrait}>
			<div id="codemirror-editor-wrapper" ref={editorDomRef} />
		</StyledCodeMirror>
	);
}

export default CodeMirror;
